import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import { OrderListAction } from '../../enums/OrderListAction';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import { DisplayIdentifier } from '../../../smoothr-web-app-core/enums/DisplayIdentifier';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import Article from '../../../smoothr-web-app-core/models/Article';
import { ValidationUtils } from '../../../smoothr-web-app-core/utils/validation-utils';
import Order from '../../../smoothr-web-app-core/models/Order';
import { getPrice, numberToCurrency } from '../../../smoothr-web-app-core/utils/utils';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { PromoCodeType } from '../../../smoothr-web-app-core/models/PromoCodeType';

@Component({
	selector: 'app-order-list',
	templateUrl: './order-list.component.html',
	styleUrls: ['order-list.component.scss'],
})
export class OrderListComponent implements DoCheck {
	@Output() action = new EventEmitter<OrderListAction>();
	@Input() editable = true;
	OrderUtils = OrderUtils;
	isSomeOptionFullPrice = false;
	needsConfiguration = false;
	articleGroupPrice = 0;
	numberToCurrency = numberToCurrency;
	getPrice = getPrice; // Expose getPrice to template to fix OrderUtils error
	private prevPromoState = false;
	private prevQuantity = 1;

	constructor(private cd: ChangeDetectorRef) {}

	private _articleGroup: ArticleGroup;

	get articleGroup(): ArticleGroup {
		return this._articleGroup;
	}

	@Input()
	set articleGroup(value: ArticleGroup) {
		this._articleGroup = this.mapGroup(value);
		this.reloadArticle();
	}

	private _order: Order;

	get order(): Order {
		return this._order;
	}

	@Input()
	set order(value: Order) {
		this._order = value;
		this._articleGroup = this.mapGroup(this.articleGroup);

		this.reloadArticle();
	}
	@Input() venue: Venue;
	mapGroup(articleGroup: ArticleGroup) {
		if (!this.venue || !this.order) {
			return articleGroup;
		}
		if (this.venue) {
			const foundArticle = this.venue.articleCategories
				.map(it => {
					if (it.visible) {
						const foundArticle = it.articles.find(ar => ar._id === articleGroup.article._id);

						return foundArticle ?? null;
					} else {
						return null;
					}
				})
				.filter(it => !!it);
			if (foundArticle.length === 1) {
				const test = JSON.parse(JSON.stringify(articleGroup)) as ArticleGroup;
				test.article = JSON.parse(JSON.stringify(foundArticle[0]));

				return test;
			}
			return articleGroup;
		}
		return articleGroup;
	}
	edit() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.edit);
	}

	isEditable(): boolean {
		return this.editable;
	}

	delete() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.delete);
	}

	async note() {
		if (!this.editable) {
			return;
		}
		this.action.emit(OrderListAction.note);
	}

	add() {
		if (!this.isEditable()) {
			return;
		}
		// Prevent adding more free articles than allowed by promo code
		if (!this.canAddMoreFreeArticle()) {
			return;
		}
		this.action.emit(OrderListAction.add);
	}

	remove() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.remove);
	}

	isOptionFullPrice(articleOption: ArticleOption): boolean {
		const group = this.articleGroup.article.groups.find(grp => grp._id === articleOption.group);
		return group && group.displayIdentifiers && group.displayIdentifiers.findIndex(di => DisplayIdentifier.fullPrice === di) >= 0;
	}

	hiddenInCart(article: Article): boolean {
		return article.tags && article.tags.find(tag => tag.identifier === 'hide_cart') !== undefined;
	}

	reloadArticle() {
		this.needsConfiguration = false;
		this.isSomeOptionFullPrice = false;
		this.articleGroupPrice = 0;
		this.prevPromoState = false;
		this.prevQuantity = 1;
		if (!this.articleGroup || !this.order || !this.order.preorder) {
			this.articleGroupPrice = 0;
			return;
		}
		this.prevQuantity = this.articleGroup.quantity;
		this.prevPromoState = this.articleGroup.isPromo;
		this.needsConfiguration = !ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups);
		for (const articleOption of this.articleGroup.groups) {
			if (this.isOptionFullPrice(articleOption)) {
				this.isSomeOptionFullPrice = true;
			}
		}
		let totalPrice = OrderUtils.totalPrice(this.articleGroup, this.order.type, this.order.preorder.type);

		const tagPrice = this.findTagPrice(this.articleGroup.article);
		if (tagPrice) {
			totalPrice = totalPrice - tagPrice;
		}
		
		// Subtract tax options from the main header price
		if (this.articleGroup.groups) {
			for (const group of this.articleGroup.groups) {
				if (group.article && this.isTax(group.article)) {
					const taxPrice = getPrice(group.article, this.order.type, this.order.preorder?.type) * group.quantity;
					totalPrice -= taxPrice;
				}
			}
		}

		this.articleGroupPrice = totalPrice;
		if (OrderUtils.isBogoOrFreeArticlePromo(this.order) && this.articleGroup.isPromo) {
			if (this.articleGroup.freeArticle) {
				// Free-article promos (FREE_ARTICLE, MULTIPLE_FREE_ARTICLES,
				// CHEAPEST_FREE_ARTICLE, DEPENDENCY_FREEE_ARTICLE) zero out the article
				// price because totalPrice short-circuits to 0 for free articles. Any Pfand
				// (deposit) is still charged and added to the order total via
				// totalPricePfandPrize, so surface that deposit on the line item to keep the
				// breakdown reconciled with the Gesamtpreis instead of showing 0,00 €.
				this.articleGroupPrice =
					OrderUtils.totalPricePfandPrize(this.articleGroup, this.order.type, this.order.preorder.type) *
					this.articleGroup.quantity;
			} else {
				this.articleGroupPrice -= OrderUtils.bogoPrice(this.articleGroup, this.order.type, this.order.preorder.type);
			}
		}
	}
	ngDoCheck(): void {
		if (this.prevPromoState !== this.articleGroup.isPromo) {
			this.reloadArticle();
			this.cd.markForCheck();
		}
		if (this.prevQuantity !== this.articleGroup.quantity) {
			this.reloadArticle();
			this.cd.markForCheck();
		}
	}

	findTagPrice(article: Article) {
		if (article && article.tags && article.tags.length > 0) {
			const foundTag = article.tags.find(
				it => it.identifier === 'deposit25' || it.identifier === 'deposit15' || it.identifier === 'deposit08'
			);
			if (foundTag) {
				return foundTag.identifier === 'deposit25'
					? 0.25
					: foundTag.identifier === 'deposit15'
						? 0.15
						: foundTag.identifier === 'deposit08'
							? 0.08
							: 0;
			}
		} else {
			return null;
		}
	}
	isTax(article: Article): boolean {
		return (
			!!article.tags?.find(tag => tag.identifier === 'take_away_required') ||
			article?.name?.de?.toLowerCase().includes('verpackungssteuer')
		);
	}

	formatTaxPrice(price: number): string {
		return price.toFixed(2).replace('.', ',') + '€';
	}

	priceOfOption(articleOption: ArticleOption): number {
		if (articleOption.group === '6615e6b444b15eee9c5e6738' || articleOption.group === '6615e6c344b15eee9c5e6e7d') {
			return 0;
		}
		if (
			articleOption &&
			(articleOption?.article?.name?.de?.includes('Pfand') ||
				articleOption?.article?.name?.de?.toLowerCase().includes('verpackungssteuer'))
		) {
			return 0;
		}
		let price = getPrice(articleOption.article, this.order.type, this.order.preorder?.type);

		if (this.articleGroup) {
			const findGroup = this.articleGroup.article.groups.find(it => it._id === articleOption.group);
			if (findGroup) {
				if (findGroup.tags.find(it => it.identifier === 'half_pizza')) {
					return 0;
				}
			}
		}

		return price * articleOption.quantity;
	}
	changeName(article: Article) {
		if (article.tags && (article.tags ?? []).find(it => it?.identifier === 'has_deposit')) {
			return 'zzgl.';
		} else if (article?.name?.de?.toLowerCase().includes('verpackungssteuer')) {
			return 'zzgl.';
		} else {
			return '';
		}
	}

	// Check if we can add more free articles (respect maxQuantity from promo)
	canAddMoreFreeArticle(): boolean {
		if (!this.articleGroup?.freeArticle || !this.articleGroup?.isPromo) {
			return true; // No restriction for non-free articles
		}
		// Get maxQuantity from the promo code
		const maxQuantity = this.order?.promoCode?.type === PromoCodeType.MULTIPLE_FREE_ARTICLES
			? (this.order.promoCode.value as any)?.maxQuantity || 1
			: 1;

		return this.articleGroup.quantity < maxQuantity;
	}
}
